import { useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import styles from "./ManageDevicesTable.module.css";
import type { Device, DeviceStatus } from "@/api/services/manageDevicesServices";

// Re-export domain types from the service so this component's consumers
// (tests, other pages) can import them from the table if convenient.
export type { Device, DeviceStatus };

/** Keys of the toggleable data columns (the checkbox column is always shown). */
export type DeviceColumnKey =
  | "status"
  | "name"
  | "location"
  | "deviceType"
  | "deviceModel";

/** Ordered column definitions used by the table and the Columns menu. */
export const DEVICE_COLUMNS: { key: DeviceColumnKey; label: string }[] = [
  { key: "status",      label: "manageDevices.columns.status" },
  { key: "name",        label: "manageDevices.columns.name" },
  { key: "location",    label: "manageDevices.columns.location" },
  { key: "deviceType",  label: "manageDevices.columns.deviceType" },
  { key: "deviceModel", label: "manageDevices.columns.deviceModel" },
];

export interface ManageDevicesTableProps {
  devices: Device[];
  emptyMessage?: string;
  /** When true, renders shimmering skeleton rows instead of data/empty state. */
  isLoading?: boolean;
  /**
   * Controlled selection. When provided together with `onSelectionChange`,
   * the parent owns which rows are selected. If omitted, the table manages
   * its own selection state internally.
   */
  selectedIds?: Set<string>;
  onSelectionChange?: (next: Set<string>) => void;
  /**
   * Which data columns are visible. When omitted, all columns are shown.
   */
  visibleColumns?: Set<DeviceColumnKey>;
  /**
   * Left-to-right column order. When omitted, the default DEVICE_COLUMNS order
   * is used.
   */
  columnOrder?: DeviceColumnKey[];
}

interface StatusBadgeProps {
  status: DeviceStatus;
}

const StatusBadge = ({ status }: StatusBadgeProps) => {
  const { t } = useTranslation();
  return (
    <span
      className={`${styles.statusBadge} ${
        status === "online" ? styles.statusOnline : styles.statusOffline
      }`}
    >
      {t(`manageDevices.statusOptions.${status}`)}
    </span>
  );
};

// Per-column header/cell classes + cell renderer. Keeps the exact markup each
// column had before, but lets us render them in an arbitrary order.
const COLUMN_CONFIG: Record<
  DeviceColumnKey,
  {
    label: string;
    headerClassName: string;
    cellClassName: string;
    renderCell: (device: Device) => ReactNode;
  }
> = {
  status: {
    label: "manageDevices.columns.status",
    headerClassName: styles.statusColumn,
    cellClassName: styles.statusCell,
    renderCell: (d) => <StatusBadge status={d.status} />,
  },
  name: {
    label: "manageDevices.columns.name",
    headerClassName: styles.nameColumn,
    cellClassName: styles.nameCell,
    renderCell: (d) => (
      <button type="button" className={styles.nameLink}>
        {d.name}
      </button>
    ),
  },
  location: {
    label: "manageDevices.columns.location",
    headerClassName: styles.locationColumn,
    cellClassName: styles.locationCell,
    renderCell: (d) => d.location,
  },
  deviceType: {
    label: "manageDevices.columns.deviceType",
    headerClassName: styles.typeColumn,
    cellClassName: styles.typeCell,
    renderCell: (d) => d.deviceType,
  },
  deviceModel: {
    label: "manageDevices.columns.deviceModel",
    headerClassName: styles.modelColumn,
    cellClassName: styles.modelCell,
    renderCell: (d) => d.deviceModel,
  },
};

export const ManageDevicesTable = ({
  devices,
  emptyMessage = "No data found",
  isLoading = false,
  selectedIds: controlledSelectedIds,
  onSelectionChange,
  visibleColumns,
  columnOrder,
}: ManageDevicesTableProps) => {
  const { t } = useTranslation();
  const [internalSelectedIds, setInternalSelectedIds] = useState<Set<string>>(
    new Set()
  );

  const isControlled = controlledSelectedIds !== undefined;
  const selectedIds = isControlled
    ? controlledSelectedIds
    : internalSelectedIds;

  const show = (key: DeviceColumnKey) =>
    visibleColumns === undefined || visibleColumns.has(key);

  const order = columnOrder ?? DEVICE_COLUMNS.map((c) => c.key);
  const orderedVisible = order.filter(show);

  // The checkbox column is always present alongside the visible data columns.
  const columnCount = 1 + orderedVisible.length;

  const commitSelection = (next: Set<string>) => {
    if (isControlled) {
      onSelectionChange?.(next);
    } else {
      setInternalSelectedIds(next);
      onSelectionChange?.(next);
    }
  };

  const allSelected = useMemo(
    () => devices.length > 0 && devices.every((d) => selectedIds.has(d.id)),
    [devices, selectedIds]
  );

  const toggleAll = () => {
    const next = new Set(selectedIds);
    if (allSelected) {
      // Deselect only the currently visible rows; leave other selections intact.
      devices.forEach((d) => next.delete(d.id));
    } else {
      // Select all currently visible rows in addition to any existing selection.
      devices.forEach((d) => next.add(d.id));
    }
    commitSelection(next);
  };

  const toggleOne = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    commitSelection(next);
  };

  return (
    <div className={styles.tableContainer}>
      <div className={styles.tableScroll}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.checkboxColumn}>
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={allSelected}
                  onChange={toggleAll}
                  aria-label={t("manageDevices.ariaLabels.selectAllDevices")}
                  disabled={devices.length === 0}
                />
              </th>
              {orderedVisible.map((key) => (
                <th key={key} className={COLUMN_CONFIG[key].headerClassName}>
                  {t(COLUMN_CONFIG[key].label)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, rowIndex) => (
                <tr key={`skeleton-${rowIndex}`} className={styles.deviceRow}>
                  <td className={styles.checkboxCell}>
                    <div
                      className={[styles.loadingSkeleton, styles.loadingSkeletonCheckbox].join(' ')}
                    />
                  </td>
                  {orderedVisible.map((key) => (
                    <td
                      key={key}
                      className={COLUMN_CONFIG[key].cellClassName}
                    >
                      <div className={styles.loadingSkeleton} />
                    </td>
                  ))}
                </tr>
              ))
            ) : devices.length === 0 ? (
              <tr>
                <td colSpan={columnCount} className={styles.emptyCell}>
                  <div className={styles.emptyState}>{emptyMessage}</div>
                </td>
              </tr>
            ) : (
              devices.map((device) => {
                const isSelected = selectedIds.has(device.id);
                return (
                  <tr
                    key={device.id}
                    className={`${styles.deviceRow} ${
                      isSelected ? styles.deviceRowSelected : ""
                    }`}
                  >
                    <td className={styles.checkboxCell}>
                      <input
                        type="checkbox"
                        className={styles.checkbox}
                        checked={isSelected}
                        onChange={() => toggleOne(device.id)}
                        aria-label={t("manageDevices.ariaLabels.selectDevice", {
                          name: device.name,
                        })}
                      />
                    </td>
                    {orderedVisible.map((key) => (
                      <td key={key} className={COLUMN_CONFIG[key].cellClassName}>
                        {COLUMN_CONFIG[key].renderCell(device)}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ManageDevicesTable;
